import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useLang } from "@/contexts/LanguageContext";
import { ThemeToggle, LanguageToggle } from "@/components/Toggles";
import api from "@/lib/api";
import { Loader2, MessageCircle, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";

const formatError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return JSON.stringify(d);
};

export default function ForgotPassword() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = phone, 2 = code + new password, 3 = success
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const isAr = lang === "ar";
  const T = {
    title: isAr ? "إعادة تعيين كلمة المرور" : "Reset Your Password",
    subtitle: isAr
      ? "استلم رمز إعادة التعيين عبر واتساب في ثوانٍ"
      : "Get a reset code via WhatsApp in seconds",
    phoneLabel: isAr ? "رقم واتساب" : "WhatsApp Phone",
    phoneHint: isAr ? "أدخل رمز الدولة — مثال +249 للسودان" : "Include country code — e.g. +249 for Sudan",
    sendCode: isAr ? "إرسال رمز واتساب" : "Send WhatsApp Code",
    codeLabel: isAr ? "الرمز المكوّن من 6 أرقام" : "6-Digit Code",
    codeHint: isAr ? "تحقق من رسائل واتساب لديك" : "Check your WhatsApp messages",
    newPwLabel: isAr ? "كلمة المرور الجديدة" : "New Password",
    confirmPwLabel: isAr ? "تأكيد كلمة المرور" : "Confirm Password",
    reset: isAr ? "إعادة تعيين كلمة المرور" : "Reset Password",
    successTitle: isAr ? "تم! يمكنك تسجيل الدخول الآن" : "Success! You can sign in now",
    successBody: isAr
      ? "تم تحديث كلمة المرور الخاصة بك. استخدم كلمة المرور الجديدة لتسجيل الدخول."
      : "Your password has been updated. Use your new password to sign in.",
    goToLogin: isAr ? "الذهاب لتسجيل الدخول" : "Go to Login",
    back: isAr ? "رجوع" : "Back",
    resend: isAr ? "إرسال الرمز مرة أخرى" : "Resend code",
    mismatch: isAr ? "كلمتا المرور غير متطابقتين" : "Passwords do not match",
    tooShort: isAr ? "كلمة المرور يجب ألا تقل عن 6 أحرف" : "Password must be at least 6 characters",
  };

  const requestCode = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!phone || phone.length < 8) {
      setErr(isAr ? "رقم غير صالح" : "Invalid phone number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/request", { phone });
      setStep(2);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e?.preventDefault();
    setErr("");
    if (newPw.length < 6) {
      setErr(T.tooShort);
      return;
    }
    if (newPw !== confirmPw) {
      setErr(T.mismatch);
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/reset", {
        phone,
        code,
        new_password: newPw,
      });
      setStep(3);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="forgot-password-page" dir={isAr ? "rtl" : "ltr"}>
      {/* Top-right toggles */}
      <div className="absolute top-6 right-6 flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)] mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> {T.back}
        </Link>

        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(37,211,102,0.15)" }}>
            {step === 3 ? (
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            ) : step === 2 ? (
              <KeyRound className="w-8 h-8 text-emerald-500" />
            ) : (
              <MessageCircle className="w-8 h-8 text-emerald-500" />
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--so-text)]">
            {step === 3 ? T.successTitle : T.title}
          </h1>
          <p className="text-sm text-[var(--so-text-soft)] mt-2">
            {step === 3 ? T.successBody : T.subtitle}
          </p>
        </div>

        <div className="so-card p-6 md:p-8">
          {/* STEP 1: Phone input */}
          {step === 1 && (
            <form onSubmit={requestCode} className="space-y-4" data-testid="forgot-step-phone">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[var(--so-text-soft)]">
                  {T.phoneLabel}
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+249xxxxxxxxx"
                  className="so-input mt-1"
                  data-testid="forgot-phone-input"
                  dir="ltr"
                />
                <p className="text-xs text-[var(--so-text-soft)] mt-2">{T.phoneHint}</p>
              </div>

              {err && (
                <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="forgot-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white transition-all disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}
                data-testid="forgot-send-code"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                {T.sendCode}
              </button>
            </form>
          )}

          {/* STEP 2: Code + new password */}
          {step === 2 && (
            <form onSubmit={resetPassword} className="space-y-4" data-testid="forgot-step-reset">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[var(--so-text-soft)]">
                  {T.codeLabel}
                </label>
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="so-input mt-1 tracking-[0.4em] text-center text-lg font-semibold"
                  placeholder="000000"
                  data-testid="forgot-code-input"
                  dir="ltr"
                />
                <p className="text-xs text-[var(--so-text-soft)] mt-2">{T.codeHint}</p>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[var(--so-text-soft)]">
                  {T.newPwLabel}
                </label>
                <input
                  type="password"
                  required
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder="••••••••"
                  className="so-input mt-1"
                  data-testid="forgot-new-password"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[var(--so-text-soft)]">
                  {T.confirmPwLabel}
                </label>
                <input
                  type="password"
                  required
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="••••••••"
                  className="so-input mt-1"
                  data-testid="forgot-confirm-password"
                />
              </div>

              {err && (
                <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="forgot-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white transition-all disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)" }}
                data-testid="forgot-reset-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {T.reset}
              </button>

              <button
                type="button"
                onClick={requestCode}
                disabled={loading}
                className="w-full text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]"
                data-testid="forgot-resend-code"
              >
                {T.resend}
              </button>
            </form>
          )}

          {/* STEP 3: Success */}
          {step === 3 && (
            <div className="text-center space-y-4" data-testid="forgot-step-success">
              <button
                onClick={() => navigate("/login")}
                className="so-btn-primary w-full justify-center"
                data-testid="forgot-go-to-login"
              >
                {T.goToLogin}
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 text-sm text-center text-[var(--so-text-soft)]">
          <Link to="/login" className="text-[var(--so-orange)] font-semibold">
            {isAr ? "العودة لتسجيل الدخول" : "Back to Login"}
          </Link>
        </div>
      </div>
    </div>
  );
}
